import { BotId, FeedbotFile } from '@feedyou-ai/feedbot-schema'

import { FeedbotThunk } from '../../store/types'
import { getApiUrl, getBotsApiUrl } from '../../apiService'
import { serverApi, ServerApiTag } from '../../store/serverApi'

type GetFilesResponse = { files: FeedbotFile[]; migrated: boolean }

export const mediaApi = serverApi.injectEndpoints({
  endpoints: builder => ({
    getMediaGalleryFiles: builder.query<GetFilesResponse, { botId: BotId }>({
      query: ({ botId }) => ({
        url: `${getBotsApiUrl()}/${botId}/media/files`,
        method: 'GET'
      }),
      providesTags: [ServerApiTag.FILES]
    }),
    uploadFileToMediaGallery: builder.mutation<
      FeedbotFile,
      { botId: BotId; filename: string; base64content: string }
    >({
      query: ({ botId, filename, base64content }) => ({
        url: `${getBotsApiUrl()}/${botId}/media/files/${filename}`,
        method: 'POST',
        body: { base64content }
      }),
      onQueryStarted: async ({ botId }, { dispatch, queryFulfilled }) => {
        try {
          const result = await queryFulfilled
          dispatch(mergeMediaGalleryFileIntoCache(botId, result.data))
        } catch (e) {}
      },
      invalidatesTags: [ServerApiTag.FILES]
    }),
    deleteFileFromMediaGallery: builder.mutation<void, { botId: BotId; filename: string }>({
      query: ({ botId, filename }) => ({
        url: `${getBotsApiUrl()}/${botId}/media/files/${filename}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_, __, args) => [
        ServerApiTag.FILES,
        ServerApiTag.FILE_CONTENT_TYPE(args.botId, 'ALL')
      ]
    }),
    migrateMediaGallery: builder.mutation<string, { botId: BotId }>({
      query: ({ botId }) => ({
        method: 'POST',
        responseHandler: 'text',
        headers: { accept: 'text/html' },
        url: getApiUrl(`/bots/${botId}/media/migrate`)
      }),
      invalidatesTags: [ServerApiTag.FILES]
    }),
    getFileContentType: builder.query<string | null, { botId: BotId; publicUrl: string }>({
      query: ({ botId, publicUrl }) => ({
        method: 'GET',
        url: getApiUrl(`/bots/${botId}/media/get-content-type`),
        params: { publicFileUrl: btoa(publicUrl) }
      }),
      transformResponse(baseQueryReturnValue: { contentType: string | null }, meta, arg) {
        return baseQueryReturnValue.contentType
      },
      providesTags: (_, __, args) => [
        ServerApiTag.FILE_CONTENT_TYPE(args.botId, 'ALL'),
        ServerApiTag.FILE_CONTENT_TYPE(args.botId, args.publicUrl)
      ]
    })
  })
})

export function mergeMediaGalleryFileIntoCache(botId: BotId, file: FeedbotFile): FeedbotThunk {
  return dispatch => {
    dispatch(
      mediaApi.util.updateQueryData(
        'getMediaGalleryFiles',
        { botId },
        (draft: GetFilesResponse) => {
          draft.files.push(file)
        }
      )
    )
  }
}
